import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import { getCampaigns } from '../../../api/campaignApi';
import { getOrders } from '../../../api/orderApi';
import Select from 'react-select';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import { Order } from '../../../types/order';
import { Campaign } from '../../../types/campaign';
import { sendSupportEmail } from '../../../api/supportApi';

const SUBJECT_OPTIONS = [
    { value: 'Sugestão', label: 'Sugestão' },
    { value: 'Elogio', label: 'Elogio' },
    { value: 'Reclamação', label: 'Reclamação' },
    { value: 'Financeiro', label: 'Financeiro' },
    { value: 'Suporte', label: 'Suporte' },
    { value: 'Outros', label: 'Outros' },
];

const initialValues = {
    campaign: null,
    order: null,
    subject: null,
    message: '',
    actionReadWiki: '',
    actionTalkedWithChatBot: '',
    files: [],
};

const validate = (values: any) => {
    const errors: any = {};
    if (!values.subject) {
        errors.subject = 'Assunto é obrigatório';
    }
    if (!values.message) {
        errors.message = 'Mensagem é obrigatória';
    }
    if (!values.actionReadWiki) {
        errors.actionReadWiki = 'Selecione uma opção';
    }
    if (!values.actionTalkedWithChatBot) {
        errors.actionTalkedWithChatBot = 'Selecione uma opção';
    }
    return errors;
};

const Index = (props: any) => {
    document.title = 'Suporte | Ad Pulse';

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        getCampaigns().then((data: any) => setCampaigns(data || []));
    }, []);

    const handleCampaignChange = (option: any, setFieldValue: Function) => {
        setFieldValue('campaign', option);
        if (option && option.value) {
            getOrders({ campaign: option.value }).then((data: any) => setOrders(data || []));
        } else {
            setOrders([]);
        }
        setFieldValue('order', null);
    };

    const campaignOptions = campaigns.map((c: Campaign) => ({
        value: `CAMPANHA ID: ${c.id} | CAMPANHA: ${c.name}`,
        label: `CAMPANHA ID: ${c.id} | CAMPANHA: ${c.name}`,
    }));

    const orderOptions = orders.map((o: Order) => ({
        value: `PEDIDO ID: ${o.id} | CAMPANHA: ${o?.campaign?.name} | PUBLISHER: ${o?.site?.publisher?.name} | TIPO: ${o.adType} | TAMANHOS: ${o.sizes.join(
            ', ',
        )}`,
        label: `PEDIDO ID: ${o.id} | CAMPANHA: ${o?.campaign?.name} | PUBLISHER: ${o?.site?.publisher?.name} | TIPO: ${o.adType} | TAMANHOS: ${o.sizes.join(
            ', ',
        )}`,
    }));

    const handleSubmit = async (values: any, { resetForm }: any) => {
        setSending(true);
        try {
            const formData = new FormData();
            formData.append('campaign', values.campaign ? values.campaign.value : '');
            formData.append('order', values.order ? values.order.value : '');
            formData.append('subject', values.subject ? values.subject.value : '');
            formData.append('message', values.message);
            formData.append('actionReadWiki', values.actionReadWiki);
            formData.append('actionTalkedWithChatBot', values.actionTalkedWithChatBot);

            values.files.forEach((file: File) => {
                formData.append('files', file);
            });

            await sendSupportEmail(formData);
            resetForm();
            toast.success('Mensagem enviada com sucesso!');
        } catch {
            toast.error('Erro ao enviar. Tente novamente.');
        } finally {
            setSending(false);
        }
    };

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: Function,
        values: any,
    ) => {
        const files = Array.from(e.target.files || []);
        const allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'video/mp4',
            'video/mpeg',
            'video/quicktime',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/zip',
            'application/x-zip-compressed',
        ];

        const validFiles = files.filter((file) => allowedTypes.includes(file.type));
        const newFiles = [...values.files, ...validFiles];

        if (validFiles.length !== files.length) {
            toast.error(
                'Alguns arquivos não são permitidos. Apenas imagens, vídeos, PDF, Word e ZIP são aceitos.',
            );
        }

        setFieldValue('files', newFiles);
    };

    const removeFile = (index: number, setFieldValue: Function, values: any) => {
        const newFiles = values.files.filter((_: File, i: number) => i !== index);
        setFieldValue('files', newFiles);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">Suporte</h5>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <Formik
                                        initialValues={initialValues}
                                        validate={validate}
                                        onSubmit={handleSubmit}
                                    >
                                        {({
                                            values,
                                            setFieldValue,
                                            handleChange,
                                            handleBlur,
                                            errors,
                                            touched,
                                        }) => (
                                            <Form>
                                                <div className="mb-3">
                                                    <label className="form-label">Campanha</label>
                                                    <Select
                                                        id="campaign"
                                                        name="campaign"
                                                        options={campaignOptions}
                                                        value={values.campaign}
                                                        onChange={(option) =>
                                                            handleCampaignChange(option, setFieldValue)
                                                        }
                                                        placeholder="Selecione uma campanha"
                                                        isClearable
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Pedido</label>
                                                    <Select
                                                        id="order"
                                                        name="order"
                                                        options={orderOptions}
                                                        value={values.order}
                                                        onChange={(option) =>
                                                            setFieldValue('order', option)
                                                        }
                                                        placeholder="Selecione um pedido"
                                                        isClearable
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Assunto</label>
                                                    <Select
                                                        id="subject"
                                                        name="subject"
                                                        options={SUBJECT_OPTIONS}
                                                        value={values.subject}
                                                        onChange={(option) =>
                                                            setFieldValue('subject', option)
                                                        }
                                                        placeholder="Selecione o assunto"
                                                        isClearable
                                                        styles={{
                                                            control: (baseStyles, state) => ({
                                                                ...baseStyles,
                                                                borderColor:
                                                                    touched.subject && errors.subject
                                                                        ? 'red'
                                                                        : '#ced4da',
                                                            }),
                                                        }}
                                                    />
                                                    <ErrorMessage name="subject">
                                                        {(msg) => (
                                                            <div className="text-danger mt-1">
                                                                {msg}
                                                            </div>
                                                        )}
                                                    </ErrorMessage>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Mensagem</label>
                                                    <Field
                                                        as="textarea"
                                                        className={`form-control${
                                                            touched.message && errors.message
                                                                ? ' is-invalid'
                                                                : ''
                                                        }`}
                                                        name="message"
                                                        rows={6}
                                                        required
                                                    />
                                                    <ErrorMessage name="message">
                                                        {(msg) => (
                                                            <div className="invalid-feedback d-block">
                                                                {msg}
                                                            </div>
                                                        )}
                                                    </ErrorMessage>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Anexos</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        multiple
                                                        accept="image/*,video/*,.pdf,.doc,.docx,.zip"
                                                        onChange={(e) =>
                                                            handleFileChange(e, setFieldValue, values)
                                                        }
                                                    />
                                                    <div className="form-text">
                                                        Arquivos permitidos: Imagens, Vídeos, PDF,
                                                        Word, ZIP
                                                    </div>
                                                    {values.files.length > 0 && (
                                                        <div className="mt-2">
                                                            <label className="form-label">
                                                                Arquivos selecionados:
                                                            </label>
                                                            <ul className="list-group">
                                                                {values.files.map(
                                                                    (file: File, index: number) => (
                                                                        <li
                                                                            key={index}
                                                                            className="list-group-item d-flex justify-content-between align-items-center"
                                                                        >
                                                                            <span>
                                                                                {file.name} (
                                                                                {(
                                                                                    file.size /
                                                                                    1024 /
                                                                                    1024
                                                                                ).toFixed(2)}{' '}
                                                                                MB)
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() =>
                                                                                    removeFile(
                                                                                        index,
                                                                                        setFieldValue,
                                                                                        values,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i className="ri-close-line"></i>
                                                                            </button>
                                                                        </li>
                                                                    ),
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Você já tentou encontrar uma solução no nosso{' '}
                                                        <a
                                                            href="https://docs.adpulse.com.br"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            wiki
                                                        </a>
                                                        ?
                                                    </label>
                                                    <div>
                                                        <div className="form-check form-check-inline">
                                                            <Field
                                                                type="radio"
                                                                className="form-check-input"
                                                                name="actionReadWiki"
                                                                id="actionReadWikiSim"
                                                                value="true"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="actionReadWikiSim"
                                                            >
                                                                Sim
                                                            </label>
                                                        </div>
                                                        <div className="form-check form-check-inline">
                                                            <Field
                                                                type="radio"
                                                                className="form-check-input"
                                                                name="actionReadWiki"
                                                                id="actionReadWikiNao"
                                                                value="false"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="actionReadWikiNao"
                                                            >
                                                                Não
                                                            </label>
                                                        </div>
                                                        <ErrorMessage name="actionReadWiki">
                                                            {(msg) => (
                                                                <div className="text-danger mt-1">
                                                                    {msg}
                                                                </div>
                                                            )}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Você já tentou resolver o problema com o
                                                        chatbot?
                                                    </label>
                                                    <div>
                                                        <div className="form-check form-check-inline">
                                                            <Field
                                                                type="radio"
                                                                className="form-check-input"
                                                                name="actionTalkedWithChatBot"
                                                                id="actionTalkedWithChatBotSim"
                                                                value="true"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="actionTalkedWithChatBotSim"
                                                            >
                                                                Sim
                                                            </label>
                                                        </div>
                                                        <div className="form-check form-check-inline">
                                                            <Field
                                                                type="radio"
                                                                className="form-check-input"
                                                                name="actionTalkedWithChatBot"
                                                                id="actionTalkedWithChatBotNao"
                                                                value="false"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="actionTalkedWithChatBotNao"
                                                            >
                                                                Não
                                                            </label>
                                                        </div>
                                                        <ErrorMessage name="actionTalkedWithChatBot">
                                                            {(msg) => (
                                                                <div className="text-danger mt-1">
                                                                    {msg}
                                                                </div>
                                                            )}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={sending}
                                                >
                                                    {sending ? 'Enviando...' : 'Enviar'}
                                                </button>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);

